import CloseButton from '@atoms/CloseButton/CloseButton';
import Icon from '@atoms/Icon/Icon';
import Image from '@atoms/Image/Image';
import Modal from '@atoms/Modal/Modal';
import { IImage } from '@models/IImage';
import { ISignupForm } from '@models/ISignupForm';
import ErrorBannerNotificationEnum from '@models/enums/ErrorBannerNotificationEnum';
import LocalStorageKeyEnum from '@models/enums/LocalStorageKeyEnum';
import SignupForm from '@organisms/SignupForm/SignupForm';
import signupFormGetProps from '@services/signupFormGetProps';
import useCookieStatusStore from '@state/CookieStatusStore';
import useErrorNotificationStore from '@state/ErrorBannerNotificationDisplayStore';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';

interface Props {
  readonly contactList?: string;
  readonly disabled: boolean;
  readonly displayTime: number;
  readonly formEndpoint: string;
  readonly image?: IImage;
  readonly resetTime: number;
  readonly signupButtonLabel: string;
  readonly subtitle?: string;
  readonly title?: string;
}

const NewsletterSignupPopup = ({
  contactList,
  disabled,
  displayTime = 0,
  formEndpoint,
  image,
  resetTime,
  signupButtonLabel,
  subtitle,
  title,
}: Props) => {
  const [signupFormData, setSignupFormData] = useState<ISignupForm>();
  const [isSignupFormModalOpen, setIsSignupFormModalOpen] = useState(false);
  const [isPopupDisplayed, setIsPopupDisplayed] = useState(false);
  const [isDisplayDelayActive, setIsDisplayDelayActive] = useState(true);
  const { cookieStatus } = useCookieStatusStore((state) => ({
    cookieStatus: state.cookieStatus,
  }));
  const { setErrorBannerNotification } = useErrorNotificationStore();

  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentional missing dependencies
  useEffect(() => {
    if (!cookieStatus) return;

    setTimeout(() => {
      setIsDisplayDelayActive(false);
    }, displayTime * 1000);
  }, [cookieStatus]);

  const handleClose = () => {
    setIsPopupDisplayed(false);
    localStorage.setItem(
      LocalStorageKeyEnum.popupDismissedDate,
      dayjs().toISOString()
    );
  };

  const handleCTAClick = () => {
    setIsPopupDisplayed(false);
    localStorage.setItem(
      LocalStorageKeyEnum.popupDismissedDate,
      dayjs().toISOString()
    );
    setIsSignupFormModalOpen(true);
  };

  useEffect(() => {
    const popupDismissedDate = localStorage.getItem(
      LocalStorageKeyEnum.popupDismissedDate
    );

    if (
      !popupDismissedDate ||
      dayjs(popupDismissedDate).add(resetTime, 'd').diff() <= 0
    ) {
      setIsPopupDisplayed(true);
    }
  }, [resetTime]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentional Missing Dependencies
  useEffect(() => {
    const getNewsletterForm = async () => {
      try {
        const signupFormResponse = await signupFormGetProps(formEndpoint);
        setSignupFormData(signupFormResponse);
      } catch {
        setErrorBannerNotification(
          ErrorBannerNotificationEnum.NewsletterSignupPopup
        );
      }
    };

    getNewsletterForm();
  }, [formEndpoint]);

  if (disabled || isDisplayDelayActive) return null;

  return (
    <>
      <Modal
        isOpen={isPopupDisplayed}
        transparent
        showCloseButton={false}
        containerZIndexClassName="z-[1000]"
        contentClasses="flex items-center justify-center min-h-full"
        modalClasses="py-5"
      >
        <div
          data-testid="CT-05-C-Newsletter-Signup-Popup"
          className="relative w-[335px] overflow-hidden rounded-[10px] text-black-100 lg:w-[420px]"
        >
          <CloseButton
            className="absolute top-2.5 right-2.5 rounded-full bg-black-100/50 p-1 before:h-px before:w-3 after:h-px after:w-3"
            onClick={handleClose}
            color="white"
            dataTestId="CT-05-C-Newsletter-Signup-Popup-Close-Button"
          />

          {image && (
            <Image
              image={image}
              imageClasses="object-cover h-[233px] lg:h-[292px]"
              dataTestId="CT-05-C-Newsletter-Signup-Popup-Image"
            />
          )}

          <div className="flex flex-col gap-y-5 bg-white p-5">
            {title && (
              <p
                className="h5"
                data-testid="CT-05-C-Newsletter-Signup-Popup-Title"
              >
                {title}
              </p>
            )}

            {subtitle && (
              <p
                className="body-three"
                data-testid="CT-05-C-Newsletter-Signup-Popup-Subtitle"
              >
                {subtitle}
              </p>
            )}

            <button
              data-testid="CT-05-C-Newsletter-Signup-Popup-CTA"
              className="rounded-[5px] border border-grey p-[18px]"
              onClick={handleCTAClick}
            >
              <div className="relative flex items-center justify-center px-8">
                <div className="absolute left-0">
                  <Icon name="stacked_email" colorClassName="text-grey-450" />
                </div>

                <span className="body-four">{signupButtonLabel}</span>
              </div>
            </button>
          </div>
        </div>
      </Modal>

      {signupFormData && (
        <Modal
          isOpen={isSignupFormModalOpen}
          setIsOpen={setIsSignupFormModalOpen}
          showCloseButton={false}
          containerZIndexClassName="z-[1000]"
          contentClasses="py-4"
        >
          <SignupForm
            {...signupFormData}
            contactList={contactList}
            closeCallback={() =>
              setIsSignupFormModalOpen(!isSignupFormModalOpen)
            }
            recaptchaChallengeElementZIndex="1000"
            trackingComponentName="pop_up"
          />
        </Modal>
      )}
    </>
  );
};

export default NewsletterSignupPopup;
